import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { brand } from '../../config/brand';
import { MENU_GROUPS, istSichtbar } from '../../config/menu';
import menueService, { MENUE_UPDATED_EVENT } from '../../services/menueService';
import { Icon, IconName } from '../ui';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
}

const COLLAPSE_KEY = 'sidebar-collapsed-groups';

const MenueIcon: React.FC<{ name: IconName }> = ({ name }) => {
  const I = Icon[name];
  return <I className="h-5 w-5" />;
};

/**
 * Seitenleiste, nach Arbeitsbereichen gruppiert. Ab Tablet-Breite immer da: ausgeklappt mit Logo und
 * Beschriftung, eingeklappt als schmale Symbolleiste (Tropfen + Menüsymbole, Name als Tooltip). Eine
 * Kopfzeile gibt es nicht – jede Seite trägt ihren eigenen Titel. Auf dem Handy ist sie ein Overlay.
 */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sichtbarkeit, setSichtbarkeit] = useState<Record<string, boolean>>({});
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '[]')); } catch { return new Set(); }
  });

  const ladeSichtbarkeit = useCallback(() => {
    menueService.getSichtbarkeit().then(setSichtbarkeit).catch(() => {});
  }, []);

  useEffect(() => {
    ladeSichtbarkeit();
    window.addEventListener(MENUE_UPDATED_EVENT, ladeSichtbarkeit);
    return () => window.removeEventListener(MENUE_UPDATED_EVENT, ladeSichtbarkeit);
  }, [ladeSichtbarkeit]);

  const admin = isAdmin();

  const toggleGroup = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(Array.from(next))); } catch { /* egal */ }
      return next;
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Unterseiten (Detail, Formular) hängen an ihrem Bereich.
  const isActive = (path: string) =>
    location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(`${path}/`));

  // Auf dem Handy ist die Leiste ein Overlay – nach dem Navigieren schließen. Ab Tablet bleibt sie.
  const closeOnMobile = () => { if (window.innerWidth < 769) onClose(); };

  // Gruppen mit sichtbaren Items vorbereiten; leere Gruppen weglassen
  const groups = MENU_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => istSichtbar(i, admin, sichtbarkeit)) }))
    .filter((g) => g.items.length > 0);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      <aside className={`sidebar ${isOpen ? 'sidebar-open' : 'sidebar-eingeklappt'}`}>
        <div className="sidebar-header">
          {/* Das Logo führt zum Dashboard. Eingeklappt steht nur der Tropfen da. */}
          <Link to="/dashboard" onClick={closeOnMobile} className="sidebar-logo-link" aria-label="Zum Dashboard" title="Zum Dashboard">
            <img src="/hp-logo_white.png" alt={brand.shortName} className="sidebar-logo sidebar-logo--voll" />
            <img src="/hp-logo_drop.png" alt="" className="sidebar-logo sidebar-logo--tropfen" />
          </Link>
          <button
            className="sidebar-close"
            onClick={onToggle}
            aria-label={isOpen ? 'Menü einklappen' : 'Menü ausklappen'}
            title={isOpen ? 'Menü einklappen' : 'Menü ausklappen'}
          >
            {isOpen ? (
              <>
                {/* Handy: Kreuz schließt das Overlay, ab Tablet klappt der Chevron zur Symbolleiste ein. */}
                <span className="hide-desktop"><Icon.Close className="h-5 w-5" /></span>
                <span className="hide-mobile"><Icon.ChevronLeft className="h-5 w-5" /></span>
              </>
            ) : <Icon.ChevronRight className="h-5 w-5" />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {groups.map((group) => {
            const istEinzelStart = group.key === 'start';
            // In der Symbolleiste gibt es keine Überschriften zum Aufklappen – dort alles zeigen.
            const eingeklappt = isOpen && collapsed.has(group.key);
            return (
              <div key={group.key} className="sidebar-group">
                {!istEinzelStart && (
                  <button
                    onClick={() => toggleGroup(group.key)}
                    className="sidebar-group-title"
                    aria-expanded={!eingeklappt}
                  >
                    <span>{group.label}</span>
                    {eingeklappt ? <Icon.ChevronRight className="h-3.5 w-3.5" /> : <Icon.ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                )}
                {(istEinzelStart || !eingeklappt) && group.items.map((item) => {
                  const unterpunkte = (item.unterpunkte || []).filter((u) => istSichtbar(u, admin, sichtbarkeit));
                  const imBereich = isActive(item.path) || unterpunkte.some((u) => isActive(u.path));
                  return (
                    <React.Fragment key={item.path}>
                      <Link
                        to={item.path}
                        className={`sidebar-link ${imBereich ? 'active' : ''}`}
                        onClick={closeOnMobile}
                        title={isOpen ? undefined : item.label}
                      >
                        <span className="sidebar-icon"><MenueIcon name={item.icon} /></span>
                        <span className="sidebar-label">{item.label}</span>
                      </Link>
                      {/* Unterpunkte nur im Bereich und nur ausgeklappt – sonst halten sie das Menü unnötig lang. */}
                      {isOpen && imBereich && unterpunkte.map((u) => (
                        <Link
                          key={u.path}
                          to={u.path}
                          className={`sidebar-sublink ${isActive(u.path) ? 'active' : ''}`}
                          onClick={closeOnMobile}
                        >
                          {u.label}
                        </Link>
                      ))}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* User Menu */}
        <div className="sidebar-footer">
          <div className="user-section">
            <div className="user-info" onClick={() => setShowUserMenu(!showUserMenu)}>
              <div className="user-avatar">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="user-details">
                <div className="user-name">{user?.firstName} {user?.lastName}</div>
                <div className="user-role">{user?.role === 'ADMIN' ? 'Administrator' : 'Benutzer'}</div>
              </div>
              <div className="user-menu-toggle">
                {showUserMenu ? <Icon.ChevronDown className="h-4 w-4" /> : <Icon.ChevronUp className="h-4 w-4" />}
              </div>
            </div>

            {showUserMenu && (
              <div className="user-menu">
                <Link
                  to="/change-password"
                  className="user-menu-item"
                  onClick={() => { setShowUserMenu(false); closeOnMobile(); }}
                >
                  <span className="user-menu-icon"><Icon.Schluessel className="h-[18px] w-[18px]" /></span>
                  <span>Passwort ändern</span>
                </Link>
                <button className="user-menu-item" onClick={handleLogout}>
                  <span className="user-menu-icon"><Icon.Abmelden className="h-[18px] w-[18px]" /></span>
                  <span>Abmelden</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
